import { useState, useEffect, useCallback } from 'react';
import { mockActivityService, mockActivityData } from '../../services/Dashboard/mockDataService';
import { Activity, ActivityType } from '../../types/dashboard/ActivityFeed';

export const useActivityFeedMock = (userId: string) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<ActivityType | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  const loadActivities = useCallback(
    async (offset = 0, limit = 20) => {
      if (isLoading) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const newActivities = await mockActivityService.getUserActivities(
          userId,
          limit,
          offset
        );

        if (offset === 0) {
          setActivities(newActivities);
        } else {
          setActivities(prev => [...prev, ...newActivities]);
        }

        setHasMore(newActivities.length === limit);
      } catch (error) {
        console.error('Failed to load activities:', error);
        setError('Failed to load activities');
      } finally {
        setIsLoading(false);
      }
    },
    [userId, isLoading]
  );

  const markAsRead = useCallback(async (activityId: string) => {
    try {
      await mockActivityService.markActivityAsRead(activityId);
      setActivities(prev =>
        prev.map(activity =>
          activity.id === activityId
            ? { ...activity, metadata: { ...activity.metadata, isRead: true } }
            : activity
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark activity as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      setActivities(prev =>
        prev.map(activity => ({
          ...activity,
          metadata: { ...activity.metadata, isRead: true }
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all activities as read:', error);
    }
  }, []);

  const refresh = useCallback(() => {
    loadActivities(0);
  }, [loadActivities]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      loadActivities(activities.length);
    }
  }, [isLoading, hasMore, activities.length, loadActivities]);

  // Load initial data
  useEffect(() => {
    if (userId) {
      loadActivities(0);
    }
  }, [userId, loadActivities]);

  // Load unread count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const count = await mockActivityService.getUnreadActivityCount(userId);
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to load unread count:', error);
      }
    };

    if (userId) {
      loadUnreadCount();
    }
  }, [userId]);

  // Filter activities
  const filteredActivities = activities.filter(
    activity => filter === 'all' || activity.type === filter
  );

  return {
    activities: filteredActivities,
    isLoading,
    hasMore,
    unreadCount,
    filter,
    error,
    setFilter,
    loadMore,
    markAsRead,
    markAllAsRead,
    refresh
  };
};
